import { useState, useEffect } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import {
  Users,
  UserPlus,
  Search,
  Edit,
  Eye,
  AlertTriangle,
  GraduationCap,
  Bed,
  CreditCard,
  Printer,
  ArrowLeft,
  Filter,
  MoreHorizontal,
  Building2,
  Check,
  ChevronRight,
} from "lucide-react";
import ContentLoader from "../authentication/ContentLoader";
import { SidebarProvider } from "@/components/ui/sidebar";
import Header from "./Header";
import AdminSidebar from "./Sidebar";
import StudentRegistrationForm from "./forms/StudentRegistrationForm";
import RegisterOngoingStudent from "./forms/RegisterOngoingStudent";
import StudentProfile from "./StudentProfile";
import StudentIDCard from "./StudentIDCard";
import { IdCardDesigner } from "./identycards/IdCardDesigner";
import { StudentData, INITIAL_HEADER_SETTINGS } from "./identycards/types";
import StudentEditForm from "./forms/StudentEditForm";
import DisciplineManagement from "./DisciplineManagement";
import DormsManagement from "./DormsManagement";
import { toast } from "sonner";
import { IdCardConfig, DEFAULT_ID_CONFIG } from "./identycards/IdCardTypes";
import { db } from "../firebase/config";
import { 
  collection, 
  onSnapshot, 
  doc, 
  getDoc,
  setDoc,
  query,
  orderBy,
  limit,
  startAfter,
  startAt,
  endAt,
  getCountFromServer,
  where,
  QueryDocumentSnapshot,
  DocumentData
} from "firebase/firestore";
import { getAuth, onAuthStateChanged, IdTokenResult } from "firebase/auth";

interface Affiliation {
  role: string;
  schoolID?: string;
}

interface Student {
  id: string;
  registrationNumber: string;
  firstName: string;
  lastName: string;
  grade: string;
  currentClassUid?: string;
  className?: string;
  class: string;
  dormitory: string;
  status: string;
  gender: string;
  attendance: number;
  middleName?: string;
  behaviorScore?: number;
  dateOfBirth?: string;
  parentName?: string;
  parentPhone?: string;
  medicalInfo?: string;
  photoUrl?: string;
  studentType?: string; // "Boarding" or "Day School"
}

const StudentsManagement = () => {
  const navigate = useNavigate();
  const [activeView, setActiveView] = useState<"overview" | "register" | "registerOngoing" | "profile" | "edit" | "discipline" | "idCards" | "viewAll" | "dorms" | "viewIdCards">("overview");
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [formFilter, setFormFilter] = useState<string>("all");
  const [streamFilter, setStreamFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "suspended">("all");
  const [editSearchQuery, setEditSearchQuery] = useState("");
  const [students, setStudents] = useState<Student[]>([]);
  const [streams, setStreams] = useState<string[]>([]);
  const [forms, setForms] = useState<string[]>([]);
  const [schoolID, setSchoolID] = useState<string>("");
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [hasValidClaims, setHasValidClaims] = useState(false);
  const [isLoadingStudents, setIsLoadingStudents] = useState(true);

  // Pagination State
  const [lastVisible, setLastVisible] = useState<QueryDocumentSnapshot<DocumentData> | null>(null);
  const [pageStack, setPageStack] = useState<QueryDocumentSnapshot<DocumentData>[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalStudents, setTotalStudents] = useState(0);
  const itemsPerPage = 60;
  
  const [statCounts, setStatCounts] = useState({
    total: 0,
    honor: 0,
    risk: 0,
    boarding: 0
  });

  const [idCardConfig, setIdCardConfig] = useState<IdCardConfig>(DEFAULT_ID_CONFIG);
  const [pendingIdCardConfig, setPendingIdCardConfig] = useState<IdCardConfig>(DEFAULT_ID_CONFIG);
  const [schoolDetails, setSchoolDetails] = useState<{name: string, logoUrl: string, motto: string, address: string}>({
      name: "Administration Hub",
      logoUrl: "",
      motto: "Excellence in Education",
      address: "Academic Office"
  });

  useEffect(() => {
    if (!schoolID) return;
    const fetchSchoolDetails = async () => {
      try {
        const schoolDoc = await getDoc(doc(db, "schools", schoolID, "schoolDetails", "main"));
        if (schoolDoc.exists()) {
          const data = schoolDoc.data();
          setSchoolDetails({
            name: data.name || "Administration Hub",
            logoUrl: data.logoUrl || "",
            motto: data.motto || "",
            address: data.address || "Academic Office"
          });
        }
      } catch (err) {
        console.error("Error fetching school details:", err);
      }
    };
    fetchSchoolDetails();
  }, [schoolID]);

  // Fetch ID Card Config
  useEffect(() => {
    if (!schoolID) return;
    const fetchIdCardConfig = async () => {
      try {
        const configRef = doc(db, `schools/${schoolID}/headerSettings/idCardConfig`);
        const configSnap = await getDoc(configRef);
        if (configSnap.exists()) {
          const fetchedConfig = configSnap.data() as IdCardConfig;
          // Ensure we have valid defaults if some fields are missing
          const mergedConfig = { ...DEFAULT_ID_CONFIG, ...fetchedConfig };
          setIdCardConfig(mergedConfig);
          setPendingIdCardConfig(mergedConfig);
        }
      } catch (err) {
        console.error("Error fetching ID card config:", err);
      }
    };
    fetchIdCardConfig();
  }, [schoolID]);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        const verifyUser = async () => {
          try {
            await user.getIdToken(true);
            const idTokenResult: IdTokenResult = await user.getIdTokenResult(true);
            const affiliations = (idTokenResult.claims.affiliations as Affiliation[] | undefined) || [];
            const defaultAffiliation = idTokenResult.claims.defaultAffiliation as Affiliation | undefined;
            const claimSchoolID = idTokenResult.claims.schoolID as string | undefined;

            let selectedAffiliation: Affiliation | null = null;
            if (defaultAffiliation && defaultAffiliation.role === "admin" && defaultAffiliation.schoolID) {
              selectedAffiliation = defaultAffiliation;
            } else if (affiliations.length > 0) {
              selectedAffiliation = affiliations.find(aff => aff.role === "admin" && aff.schoolID) || affiliations[0];
            }

            if (selectedAffiliation?.role === "admin" && selectedAffiliation.schoolID && claimSchoolID === selectedAffiliation.schoolID) {
              setSchoolID(selectedAffiliation.schoolID);
              setHasValidClaims(true);
            } else {
               // Retry logic omitted for brevity in write_to_file but maintaining security
               setHasValidClaims(false);
            }
          } catch (error: any) {
            console.error("Error verifying user claims:", error);
            // Don't auto-navigate, just stop loading and let parent handle auth
          } finally {
            setIsAuthLoading(false);
          }
        };
        verifyUser();
      } else {
        // Don't auto-navigate, let parent handle auth
        setIsAuthLoading(false);
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    if (!schoolID || isAuthLoading || !hasValidClaims) return;

    const fetchStats = async () => {
      try {
        const studentsRef = collection(db, `schools/${schoolID}/students`);
        const totalAllSnap = await getCountFromServer(query(studentsRef));
        const alumniSnap = await getCountFromServer(query(studentsRef, where("status", "==", "Alumni")));
        const activeCount = totalAllSnap.data().count - alumniSnap.data().count;
        
        const honorSnap = await getCountFromServer(query(studentsRef, where("behaviorScore", ">=", 90)));
        const riskSnap = await getCountFromServer(query(studentsRef, where("behaviorScore", "<", 70)));
        const boardingSnap = await getCountFromServer(query(studentsRef, where("dormitory", "!=", "Day Student")));

        setStatCounts({
          total: activeCount > 0 ? activeCount : 0,
          honor: honorSnap.data().count,
          risk: riskSnap.data().count,
          boarding: boardingSnap.data().count
        });
        setTotalStudents(activeCount > 0 ? activeCount : 0);
      } catch (err) {
        console.error("Error fetching stats:", err);
      }
    };
    fetchStats();
  }, [schoolID, isAuthLoading, hasValidClaims]);

  // Fetch Students with Pagination and Search
  useEffect(() => {
    if (!schoolID || isAuthLoading || !hasValidClaims) return;

    setIsLoadingStudents(true);
    const studentsCollectionRef = collection(db, `schools/${schoolID}/students`);
    let q = query(studentsCollectionRef, orderBy("firstName"));

    if (searchQuery.trim()) {
      // SEARCH MODE: Filter by name/reg number range
      // Reset pagination implicitly by ignoring pageStack when searching
      q = query(q, startAt(searchQuery), endAt(searchQuery + '\uf8ff'));
      
      // OPTIONAL: Limit search results to a reasonable number to prevent massive downloads
      q = query(q, limit(itemsPerPage)); 
    } else {
      // PAGINATION MODE: Standard paged view
      if (currentPage > 1 && pageStack.length >= currentPage - 1) {
         q = query(q, startAfter(pageStack[currentPage - 2]), limit(itemsPerPage));
      } else {
         q = query(q, limit(itemsPerPage));
      }
    }

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const studentsData: Student[] = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      } as Student));
      
      setStudents(studentsData);
      
      // Update cursor for NEXT page only
      if (snapshot.docs.length > 0) {
          setLastVisible(snapshot.docs[snapshot.docs.length - 1]);
      } else {
          setLastVisible(null);
      }
      
      setIsLoadingStudents(false);
    }, (error) => {
      console.error("Error fetching students:", error);
      setIsLoadingStudents(false);
    });

    return () => unsubscribe();
  }, [schoolID, isAuthLoading, hasValidClaims, searchQuery, currentPage]);

  useEffect(() => {
    if (!schoolID || isAuthLoading || !hasValidClaims) return;
    const classesCollectionRef = collection(db, `schools/${schoolID}/classes`);
    const unsubscribe = onSnapshot(classesCollectionRef, (snapshot) => {
      const streamSet = new Set<string>();
      const formSet = new Set<string>();
      snapshot.docs.forEach((doc) => {
        const data = doc.data();
        if (data.stream) streamSet.add(data.stream);
        if (data.namingConvention && data.level !== undefined) {
           formSet.add(`${data.namingConvention} ${data.level}`);
        }
      });
      setStreams(Array.from(streamSet).sort());
      setForms(Array.from(formSet).sort());
    });
    return () => unsubscribe();
  }, [schoolID, isAuthLoading, hasValidClaims]);

  const filteredStudents = students.filter(student => {
    const matchesForm = formFilter === "all" || (student.className || student.grade).toLowerCase().includes(formFilter.toLowerCase());
    const matchesStream = streamFilter === "all" || (student.class || "").toLowerCase().includes(streamFilter.toLowerCase());
    const matchesStatus = statusFilter === "all" || student.status.toLowerCase() === statusFilter.toLowerCase();
    return matchesForm && matchesStream && matchesStatus;
  });

  const handleNextPage = () => {
      if (students.length < itemsPerPage) return;
      if (lastVisible) {
          setPageStack(prev => [...prev, lastVisible]);
          setCurrentPage(prev => prev + 1);
      }
  };

  const handlePrevPage = () => {
      if (currentPage > 1) {
          setPageStack(prev => prev.slice(0, -1));
          setCurrentPage(prev => prev - 1);
      }
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
    setPageStack([]);
  };

  const handleViewStudent = (student: Student) => {
    setSelectedStudent(student);
    setActiveView("profile");
  };

  const handleEditStudent = (student: Student) => {
    setSelectedStudent(student);
    setActiveView("edit");
  };

  const handleEditByRegistrationNumber = () => {
    const student = students.find(s => s.registrationNumber.toLowerCase().includes(editSearchQuery.toLowerCase().trim()));
    if (student) {
      setSelectedStudent(student);
      setActiveView("edit");
      setEditSearchQuery("");
      toast.success(`Found student: ${student.firstName} ${student.lastName}`);
    } else {
      toast.error("Student not found");
    }
  };

  const handleUpdateStudent = async (updatedStudent: Student) => {
    try {
      const { id, ...studentData } = updatedStudent;
      const studentDocRef = doc(db, `schools/${schoolID}/students`, id);
      await setDoc(studentDocRef, studentData, { merge: true });
      setStudents(prev => prev.map(s => s.id === id ? updatedStudent : s));
      setActiveView("overview");
      toast.success("Updated successfully");
    } catch (error) {
      toast.error("Error updating student");
    }
  };

  const handleSearchStudent = () => {
    if (filteredStudents.length > 0) handleViewStudent(filteredStudents[0]);
    else toast.error("No students found");
  };

  const handleClearFilters = () => {
    setSearchQuery("");
    setFormFilter("all");
    setStreamFilter("all");
    setStatusFilter("all");
  };

  const renderOverview = () => {
    const stats = [
      { 
        title: "Active Enrollment", 
        value: statCounts.total, 
        icon: Users, 
        color: "text-blue-600", 
        bg: "bg-blue-600/10",
        desc: "Current students" 
      },
      { 
        title: "Honor Roll", 
        value: statCounts.honor, 
        icon: Check, 
        color: "text-emerald-600", 
        bg: "bg-emerald-600/10",
        desc: "Above 90 behavior" 
      },
      { 
        title: "Behavior Risk", 
        value: statCounts.risk, 
        icon: AlertTriangle, 
        color: "text-amber-600", 
        bg: "bg-amber-600/10",
        desc: "Below 70 behavior" 
      },
      { 
        title: "Boarding", 
        value: statCounts.boarding, 
        icon: Building2, 
        color: "text-indigo-600", 
        bg: "bg-indigo-600/10",
        desc: "Resident students" 
      }
    ];

    return (
      <div className="space-y-6 animate-in fade-in duration-500">
        <div className="flex flex-col gap-6 py-6 border-b border-slate-200 dark:border-slate-800">
          {/* Header Row: Title & Primary Action */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="space-y-1">
              <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 dark:text-slate-50">Administrative Control</h1>
              <p className="text-sm font-medium text-slate-500 dark:text-slate-400">Comprehensive student database and school-wide population management.</p>
            </div>
            <Button 
              onClick={() => setActiveView("register")} 
              className="bg-indigo-600 hover:bg-indigo-700 text-white h-10 px-6 rounded-md shadow-sm active:scale-95 transition-all font-bold text-sm flex items-center gap-2"
            >
              <UserPlus className="h-4 w-4" /> 
              Register Student
            </Button>
          </div>

          {/* Control Bar: Search & Secondary Tools */}
          <div className="flex flex-col lg:flex-row items-center gap-3 w-full">
            <div className="relative group flex-1 w-full lg:max-w-2xl">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 group-focus-within:text-indigo-500 transition-colors" />
              <Input 
                placeholder="Search database by registration number, name or class..." 
                value={searchQuery} 
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearchStudent()}
                className="h-10 pl-10 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 rounded-md focus-visible:ring-indigo-500 transition-all font-medium text-sm" 
              />
            </div>

            <div className="flex items-center gap-2 w-full lg:w-auto">
              <Button 
                onClick={() => setActiveView("registerOngoing")} 
                variant="outline"
                className="h-10 px-4 rounded-md border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 font-semibold text-sm transition-all"
              >
                Ongoing Entry
              </Button>
              <Button 
                onClick={() => setActiveView("dorms")} 
                variant="outline"
                className="h-10 px-4 rounded-md border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 font-semibold text-sm transition-all flex items-center gap-2"
              >
                <Bed className="h-4 w-4" /> 
                Housing
              </Button>
              <Button 
                onClick={() => navigate("/school-alumni")} 
                variant="outline"
                className="h-10 px-4 rounded-md border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 font-semibold text-sm transition-all flex items-center gap-2"
              >
                <GraduationCap className="h-4 w-4" /> 
                Alumni
              </Button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 pt-2">
          {stats.map((stat, index) => (
            <Card key={index} className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 shadow-sm hover:shadow-md transition-all group overflow-hidden relative">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className={`p-2.5 rounded-xl ${stat.bg} ${stat.color} transition-transform group-hover:scale-110`}>
                    <stat.icon className="h-5 w-5" />
                  </div>
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{stat.desc}</span>
                </div>
                <div className="space-y-1">
                  <h3 className="text-3xl font-bold text-slate-900 dark:text-slate-50 tracking-tight">{stat.value.toLocaleString()}</h3>
                  <p className="text-[11px] font-bold text-slate-500 uppercase tracking-tighter">{stat.title}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>



        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 pt-4">
          <Card onClick={() => setActiveView("viewAll")} className="cursor-pointer group hover:border-blue-500/50 transition-all bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 shadow-sm hover:shadow-lg">
            <CardHeader className="p-6 pb-0 tracking-tight flex flex-row items-center justify-between space-y-0">
              <div className="p-3 w-fit rounded-2xl bg-blue-50 dark:bg-blue-900/20 group-hover:bg-blue-500 group-hover:text-white transition-all">
                <Eye className="h-6 w-6 text-blue-500 group-hover:text-white" />
              </div>
            </CardHeader>
            <CardContent className="p-6 pt-4">
              <h4 className="font-extrabold text-lg text-slate-900 dark:text-slate-50 tracking-tight">Student Directory</h4>
              <p className="text-sm font-medium text-slate-500 mt-1.5 leading-relaxed">Access the consolidated institutional database with advanced filters and export tools.</p>
              <div className="mt-4 flex items-center text-blue-600 dark:text-blue-400 font-bold text-xs opacity-0 group-hover:opacity-100 transition-all translate-y-1 group-hover:translate-y-0">
                View Directory <ChevronRight className="ml-1 h-3.5 w-3.5" />
              </div>
            </CardContent>
          </Card>

          <Card onClick={() => setActiveView("discipline")} className="cursor-pointer group hover:border-amber-500/50 transition-all bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 shadow-sm hover:shadow-lg">
            <CardHeader className="p-6 pb-0 tracking-tight flex flex-row items-center justify-between space-y-0">
              <div className="p-3 w-fit rounded-2xl bg-amber-50 dark:bg-amber-900/20 group-hover:bg-amber-500 group-hover:text-white transition-all">
                <AlertTriangle className="h-6 w-6 text-amber-500 group-hover:text-white" />
              </div>
            </CardHeader>
            <CardContent className="p-6 pt-4">
              <h4 className="font-extrabold text-lg text-slate-900 dark:text-slate-50 tracking-tight">Conduct & Discipline</h4>
              <p className="text-sm font-medium text-slate-500 mt-1.5 leading-relaxed">Review behavioral logs, manage incident reports, and track disciplinary follow-ups.</p>
              <div className="mt-4 flex items-center text-amber-600 dark:text-amber-400 font-bold text-xs opacity-0 group-hover:opacity-100 transition-all translate-y-1 group-hover:translate-y-0">
                Manage Discipline <ChevronRight className="ml-1 h-3.5 w-3.5" />
              </div>
            </CardContent>
          </Card>

          <Card onClick={() => setActiveView("idCards")} className="cursor-pointer group hover:border-indigo-500/50 transition-all bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 shadow-sm hover:shadow-lg">
            <CardHeader className="p-6 pb-0 tracking-tight flex flex-row items-center justify-between space-y-0">
              <div className="p-3 w-fit rounded-2xl bg-indigo-50 dark:bg-indigo-900/20 group-hover:bg-indigo-500 group-hover:text-white transition-all">
                <CreditCard className="h-6 w-6 text-indigo-500 group-hover:text-white" />
              </div>
            </CardHeader>
            <CardContent className="p-6 pt-4">
              <h4 className="font-extrabold text-lg text-slate-900 dark:text-slate-50 tracking-tight">ID Processing</h4>
              <p className="text-sm font-medium text-slate-500 mt-1.5 leading-relaxed">Batch generate student identity cards with integrated QR codes and secure verification.</p>
              <div className="mt-4 flex items-center text-indigo-600 dark:text-indigo-400 font-bold text-xs opacity-0 group-hover:opacity-100 transition-all translate-y-1 group-hover:translate-y-0">
                Process IDs <ChevronRight className="ml-1 h-3.5 w-3.5" />
              </div>
            </CardContent>
          </Card>

          <Card onClick={() => setActiveView("viewIdCards")} className="cursor-pointer group hover:border-emerald-500/50 transition-all bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 shadow-sm hover:shadow-lg">
            <CardHeader className="p-6 pb-0 tracking-tight flex flex-row items-center justify-between space-y-0">
              <div className="p-3 w-fit rounded-2xl bg-emerald-50 dark:bg-emerald-900/20 group-hover:bg-emerald-500 group-hover:text-white transition-all">
                <Printer className="h-6 w-6 text-emerald-500 group-hover:text-white" />
              </div>
            </CardHeader>
            <CardContent className="p-6 pt-4">
              <h4 className="font-extrabold text-lg text-slate-900 dark:text-slate-50 tracking-tight">ID Designer</h4>
              <p className="text-sm font-medium text-slate-500 mt-1.5 leading-relaxed">Customize and visualize individual student ID cards with high-fidelity design tools.</p>
              <div className="mt-4 flex items-center text-emerald-600 dark:text-emerald-400 font-bold text-xs opacity-0 group-hover:opacity-100 transition-all translate-y-1 group-hover:translate-y-0">
                Launch Designer <ChevronRight className="ml-1 h-3.5 w-3.5" />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  };

  const renderPagination = () => {
    const estimatedTotalPages = Math.ceil(totalStudents / itemsPerPage) || 1;

    return (
      <div className="flex flex-col items-center gap-4 pt-4 border-t border-[#F1F5F9] dark:border-[#1E293B] mb-4">
        <div className="text-sm text-muted-foreground text-center">
          Page {currentPage} {totalStudents > 0 ? `of ~${estimatedTotalPages}` : ''} ({filteredStudents.length} shown)
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handlePrevPage}
            disabled={currentPage === 1}
            className="px-3 py-1.5 text-sm font-medium rounded-md border border-[#F1F5F9] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#101828] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Previous
          </button>
          <span className="px-3 py-1.5 text-sm font-medium rounded-md border border-[#F1F5F9] dark:border-[#1E293B] bg-blue-600 text-white">
            {currentPage}
          </span>
          <button
            onClick={handleNextPage}
            disabled={filteredStudents.length < itemsPerPage}
            className="px-3 py-1.5 text-sm font-medium rounded-md border border-[#F1F5F9] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#101828] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Next
          </button>
        </div>
      </div>
    );
  };

  const renderViewAll = () => (
    <div className="w-full space-y-6">
      <div className="flex items-center justify-between py-4 border-b border-slate-200 dark:border-slate-800">
        <div>
          <Button variant="outline" size="sm" onClick={() => setActiveView("overview")} className="mb-2 -ml-2 text-slate-500 border-slate-200 dark:border-slate-800 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-slate-50"><ArrowLeft className="mr-2 h-4 w-4" /> Back</Button>
          <h2 className="text-xl font-bold text-slate-900 dark:text-slate-50">Student Directory</h2>
        </div>
        <Badge variant="outline" className="text-[10px] font-bold uppercase">{filteredStudents.length} Records</Badge>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="md:col-span-2 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input placeholder="Search students..." value={searchQuery} onChange={(e) => handleSearchChange(e.target.value)} className="pl-10 h-10 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 focus-visible:ring-indigo-500 text-sm font-medium shadow-sm transition-all hover:border-indigo-300 dark:hover:border-indigo-700" />
        </div>
        <Select value={formFilter} onValueChange={setFormFilter}>
          <SelectTrigger className="h-10 min-w-[140px] bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-200 text-xs font-bold shadow-sm hover:border-indigo-400 dark:hover:border-indigo-600 transition-colors focus:ring-indigo-500"><SelectValue placeholder="Filter by Form" /></SelectTrigger>
          <SelectContent className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
            <SelectItem value="all" className="font-medium focus:bg-slate-100 dark:focus:bg-slate-800">All Forms</SelectItem>
            {forms.map(f => <SelectItem key={f} value={f} className="font-medium focus:bg-slate-100 dark:focus:bg-slate-800">{f}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={(v: any) => setStatusFilter(v)}>
          <SelectTrigger className="h-10 min-w-[140px] bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-200 text-xs font-bold shadow-sm hover:border-indigo-400 dark:hover:border-indigo-600 transition-colors focus:ring-indigo-500"><SelectValue placeholder="Filter by Status" /></SelectTrigger>
          <SelectContent className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
            <SelectItem value="all" className="font-medium focus:bg-slate-100 dark:focus:bg-slate-800">All Status</SelectItem>
            <SelectItem value="active" className="font-medium focus:bg-slate-100 dark:focus:bg-slate-800 text-emerald-600 dark:text-emerald-400">Active</SelectItem>
            <SelectItem value="suspended" className="font-medium focus:bg-slate-100 dark:focus:bg-slate-800 text-rose-600 dark:text-rose-400">Suspended</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
        <Table>
          <TableHeader className="bg-slate-50 dark:bg-slate-800/50">
            <TableRow>
              <TableHead className="py-3 px-6 text-[10px] font-bold uppercase tracking-widest text-slate-400">Student</TableHead>
              <TableHead className="py-3 px-6 text-[10px] font-bold uppercase tracking-widest text-slate-400">ID Reference</TableHead>
              <TableHead className="py-3 px-6 text-[10px] font-bold uppercase tracking-widest text-slate-400">Class</TableHead>
              <TableHead className="py-3 px-6 text-[10px] font-bold uppercase tracking-widest text-slate-400">Status</TableHead>
              <TableHead className="py-3 px-6 text-right text-[10px] font-bold uppercase tracking-widest text-slate-400">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredStudents.map((student) => (
              <TableRow key={student.id} className="group border-b border-slate-100 dark:border-slate-800/50 hover:bg-slate-50 dark:hover:bg-slate-800/20 transition-colors">
                <TableCell className="py-3 px-6">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-[10px] font-bold text-slate-500 overflow-hidden border border-slate-200 dark:border-slate-700">
                      {student.photoUrl ? (
                        <img src={student.photoUrl} alt={`${student.firstName} ${student.lastName}`} className="w-full h-full object-cover" />
                      ) : (
                        <span>{student.firstName?.[0] || "?"}</span>
                      )}
                    </div>
                    <div className="text-sm font-bold text-slate-900 dark:text-slate-50">{student.firstName} {student.lastName}</div>
                  </div>
                </TableCell>
                <TableCell className="py-3 px-6 text-xs font-mono text-slate-500">{student.registrationNumber}</TableCell>
                <TableCell className="py-3 px-6 text-xs font-bold text-slate-700 dark:text-slate-300">{student.className || student.grade}</TableCell>
                <TableCell className="py-3 px-6"><Badge variant="outline" className="text-[9px] font-bold bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800">{student.status}</Badge></TableCell>
                <TableCell className="py-3 px-6 text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
                      <DropdownMenuItem onClick={() => handleViewStudent(student)} className="focus:bg-slate-100 dark:focus:bg-slate-800 font-medium">Profile</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleEditStudent(student)} className="focus:bg-slate-100 dark:focus:bg-slate-800 font-medium">Edit</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => { setSelectedStudent(student); setActiveView("viewIdCards"); }} className="focus:bg-slate-100 dark:focus:bg-slate-800 font-medium text-emerald-600 dark:text-emerald-400">Design ID</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
      {renderPagination()}
    </div>
  );

  if (activeView === "viewIdCards") {
    const s = selectedStudent;
    const previewData: StudentData = s ? {
        name: `${s.firstName} ${s.middleName ? s.middleName + ' ' : ''}${s.lastName}`,
        studentId: s.registrationNumber,
        gradeLevel: s.className || s.grade || "Not Assigned",
        stream: s.class || "",
        schoolName: schoolDetails.name || "Administration Hub",
        schoolAddress: schoolDetails.address || "Academic Office",
        academicYear: new Date().getFullYear().toString(),
        attendancePercentage: s.attendance || 100,
        classTeacher: "Not Assigned",
        subjects: [],
        principalRemark: "",
        photoUrl: s.photoUrl || "",
        headerSettings: {
            ...INITIAL_HEADER_SETTINGS,
            isEnabled: true,
            logo: {
                ...INITIAL_HEADER_SETTINGS.logo,
                url: schoolDetails?.logoUrl || null,
            },
            schoolName: {
                ...INITIAL_HEADER_SETTINGS.schoolName,
                text: schoolDetails.name || "Administration Hub",
            },
            motto: {
                ...INITIAL_HEADER_SETTINGS.motto,
                text: schoolDetails.motto || "Excellence in Education",
            }
        }
    } : {
         name: "Preview Student", 
         studentId: "0000", 
         gradeLevel: "Level", 
         schoolName: schoolDetails.name || "Administration Hub", 
         schoolAddress: schoolDetails.address || "Academic Office",
         academicYear: new Date().getFullYear().toString(), 
         subjects: [], 
         principalRemark: "", 
         attendancePercentage: 100, 
         classTeacher: "Not Assigned",
         headerSettings: {
             ...INITIAL_HEADER_SETTINGS,
             isEnabled: true,
             logo: {
                 ...INITIAL_HEADER_SETTINGS.logo,
                 url: schoolDetails?.logoUrl || null,
             }
         }
    };

    return (
      <SidebarProvider>
        <div className="flex h-screen w-full bg-slate-50 dark:bg-slate-900">
          <AdminSidebar onSectionChange={() => {}} />
          <div className="flex flex-col flex-1 h-full min-w-0">
            <Header />
            <main className="flex-1 overflow-hidden flex flex-col bg-white dark:bg-slate-900">
              <div className="flex items-center gap-2 p-4 border-b border-slate-200 dark:border-slate-800">
                <Button variant="outline" size="sm" onClick={() => setActiveView("overview")} className="gap-2 text-xs font-bold border-slate-200 dark:border-slate-800"><ArrowLeft className="h-4 w-4" /> Exit</Button>
                <h1 className="text-sm font-bold uppercase tracking-widest ml-4">Identity Visualization</h1>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="sm" className="ml-auto bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs gap-2">
                       <Check className="h-3.5 w-3.5" /> Apply Style
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 shadow-xl sm:rounded-xl">
                    <AlertDialogHeader>
                      <AlertDialogTitle className="text-slate-900 dark:text-white font-bold text-lg">Apply Card Design?</AlertDialogTitle>
                      <AlertDialogDescription className="text-slate-500 dark:text-slate-400">
                        Are you sure you want to apply this design? This configuration will be used for all student ID cards in batch processing.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="gap-2 sm:gap-0">
                      <AlertDialogCancel className="border-slate-200 dark:border-slate-700 bg-transparent hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300 font-medium rounded-lg">Cancel</AlertDialogCancel>
                      <AlertDialogAction 
                          className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-lg transition-colors"
                          onClick={async () => {
                            try {
                              if (!schoolID) {
                                toast.error("School ID not found");
                                return;
                              }
                              
                              const configRef = doc(db, `schools/${schoolID}/headerSettings/idCardConfig`);
                              await setDoc(configRef, pendingIdCardConfig);
                              
                              setIdCardConfig(pendingIdCardConfig);
                              toast.success("Design Saved & Applied", { description: "Configuration synced to cloud." });
                            } catch (error) {
                              console.error("Error saving config:", error);
                              toast.error("Failed to save design");
                            }
                      }}>
                        Apply & Save Design
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
              <div className="flex-1 overflow-hidden relative">
                <IdCardDesigner 
                  data={previewData} 
                  initialConfig={idCardConfig}
                  onConfigChange={setPendingIdCardConfig}
                />
              </div>
            </main>
          </div>
        </div>
      </SidebarProvider>
    );
  }

  return (
    <SidebarProvider>
      <div className="flex h-screen w-full bg-slate-50 dark:bg-slate-900">
        <AdminSidebar onSectionChange={() => {}} />
        <div className="flex flex-col flex-1 h-full min-w-0">
          <Header />
          <main className="flex-1 overflow-y-auto p-4 sm:p-8 bg-slate-50 dark:bg-slate-900 relative">
            <div className="max-w-[1600px] mx-auto z-10 relative">
             <ContentLoader isLoading={isAuthLoading || isLoadingStudents || !schoolID || !hasValidClaims} message="">
              {activeView === "register" && <StudentRegistrationForm onBack={() => setActiveView("overview")} />}
              {activeView === "registerOngoing" && <RegisterOngoingStudent onBack={() => setActiveView("overview")} />}
              {activeView === "profile" && selectedStudent && <StudentProfile student={selectedStudent} onBack={() => setActiveView("overview")} />}
              {activeView === "edit" && selectedStudent && <StudentEditForm student={selectedStudent as any} onBack={() => setActiveView("overview")} onSave={handleUpdateStudent as any} schoolID={schoolID} />}
              {activeView === "discipline" && <DisciplineManagement onBack={() => setActiveView("overview")} />}
              {activeView === "dorms" && <DormsManagement onBack={() => setActiveView("overview")} />}
              {activeView === "idCards" && <StudentIDCard students={students} onBack={() => setActiveView("overview")} config={idCardConfig} schoolDetails={schoolDetails} schoolId={schoolID} />}
              {activeView === "viewAll" && renderViewAll()}
              {activeView === "overview" && renderOverview()}
             </ContentLoader>
            </div>
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
};

export default StudentsManagement;